import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, MessageCircle, MapPin, Truck, Mail, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { MaskedLines, Reveal } from '../components/Motion';
import { toast } from '../components/ui/sonner';
import { CONTACT, SERVICE_OPTIONS } from '../lib/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', phone: '', email: '', company: '', service: '', message: '' };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Contact | Shiva Prints & Packs — Printing & Packaging Enquiries, Delhi';
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, phone and message.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(initial);
      toast.success("Enquiry sent! We'll get back to you shortly.");
    } catch (err) {
      toast.error('Something went wrong. Please call or WhatsApp us directly.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full bg-background border border-border px-4 py-3.5 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-accent transition-colors';

  return (
    <>
      <section data-testid="contact-hero" className="bg-primary text-primary-foreground noise">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-28">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="overline">Contact us</motion.p>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl">
            <MaskedLines onLoad lines={["Let's talk about your", <>next <span className="text-accent">enquiry</span>.</>]} />
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact info */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Reach us directly</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-md">
                The fastest way to get a quote is a quick call or WhatsApp message. Share your requirement and we&apos;ll respond promptly.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              <Reveal>
                <a href={CONTACT.tel} data-testid="contact-call" className="group flex items-center gap-4 bg-card border border-border p-5 hover:border-accent transition-colors">
                  <span className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground group-hover:bg-accent transition-colors"><Phone className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Call now</p>
                    <p className="font-display text-lg font-medium">{CONTACT.phoneDisplay}</p>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.06}>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="group flex items-center gap-4 bg-card border border-border p-5 hover:border-[#25D366] transition-colors">
                  <span className="flex h-12 w-12 items-center justify-center bg-[#25D366] text-white"><MessageCircle className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp enquiry</p>
                    <p className="font-display text-lg font-medium">Chat with us</p>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.12}>
                <a href={CONTACT.mailto} data-testid="contact-email" className="group flex items-center gap-4 bg-card border border-border p-5 hover:border-accent transition-colors">
                  <span className="flex h-12 w-12 items-center justify-center bg-muted text-primary group-hover:bg-accent group-hover:text-white transition-colors"><Mail className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Email us</p>
                    <p className="font-display text-lg font-medium break-all">{CONTACT.email}</p>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="flex items-center gap-4 bg-card border border-border p-5">
                  <span className="flex h-12 w-12 items-center justify-center bg-muted text-primary"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Location</p>
                    <p className="font-display text-lg font-medium">{CONTACT.location}</p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="flex items-center gap-4 bg-card border border-border p-5">
                  <span className="flex h-12 w-12 items-center justify-center bg-muted text-primary"><Truck className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Service availability</p>
                    <p className="font-display text-lg font-medium">{CONTACT.coverage}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Enquiry form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-card border border-border p-8 md:p-10">
                <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">Send an enquiry</h2>
                <p className="mt-2 text-muted-foreground">Fill in the form and we&apos;ll get back to you.</p>

                {done ? (
                  <div data-testid="contact-success" className="mt-8 flex flex-col items-center text-center py-12">
                    <CheckCircle2 className="h-14 w-14 text-accent" />
                    <h3 className="font-display mt-5 text-2xl font-medium">Thank you!</h3>
                    <p className="mt-2 text-muted-foreground max-w-sm">Your enquiry has been received. Our team will reach out shortly. For anything urgent, call or WhatsApp us.</p>
                    <button data-testid="contact-new-enquiry" onClick={() => setDone(false)} className="mt-6 text-sm font-semibold link-underline">Send another enquiry</button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="contact-form" className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-medium">Name *</label>
                      <input data-testid="contact-input-name" value={form.name} onChange={update('name')} placeholder="Your full name" className={`mt-2 ${inputCls}`} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone *</label>
                      <input data-testid="contact-input-phone" value={form.phone} onChange={update('phone')} placeholder="Your phone number" className={`mt-2 ${inputCls}`} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email</label>
                      <input data-testid="contact-input-email" type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" className={`mt-2 ${inputCls}`} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Company</label>
                      <input data-testid="contact-input-company" value={form.company} onChange={update('company')} placeholder="Company name" className={`mt-2 ${inputCls}`} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium">Service required</label>
                      <select data-testid="contact-input-service" value={form.service} onChange={update('service')} className={`mt-2 ${inputCls}`}>
                        <option value="">Select a service</option>
                        {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium">Message *</label>
                      <textarea data-testid="contact-input-message" value={form.message} onChange={update('message')} rows={4} placeholder="Tell us about your requirement, quantities and timeline." className={`mt-2 resize-none ${inputCls}`} />
                    </div>
                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit"
                        className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {loading ? 'Sending…' : 'Send Enquiry'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
